import { Html5QrcodeSupportedFormats } from 'html5-qrcode';

export const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_39,
];

// Tesseract OCR (lazy-loaded)
let tesseractWorker = null;
let tesseractReady = false;

export async function getOcrWorker() {
  if (tesseractWorker && tesseractReady) return tesseractWorker;
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('eng', 1, {
    logger: () => {},
  });
  await worker.setParameters({
    tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-',
    tessedit_pageseg_mode: '6',
  });
  tesseractWorker = worker;
  tesseractReady = true;
  return worker;
}

export async function terminateOcrWorker() {
  if (tesseractWorker) {
    try {
      await tesseractWorker.terminate();
    } catch (_) {
      // ignore ocr termination error
    }
    tesseractWorker = null;
    tesseractReady = false;
  }
}

// Extract barcode-like numbers from OCR
function isValidBarcode(code) {
  if (!/^\d+$/.test(code)) return false;
  if (code.length === 13) {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(code[i], 10) * (i % 2 === 0 ? 1 : 3);
    }
    return (10 - (sum % 10)) % 10 === parseInt(code[12], 10);
  }
  if (code.length === 12) {
    let sum = 0;
    for (let i = 0; i < 11; i++) {
      sum += parseInt(code[i], 10) * (i % 2 === 0 ? 3 : 1);
    }
    return (10 - (sum % 10)) % 10 === parseInt(code[11], 10);
  }
  if (code.length === 8) {
    let sum = 0;
    for (let i = 0; i < 7; i++) {
      sum += parseInt(code[i], 10) * (i % 2 === 0 ? 3 : 1);
    }
    return (10 - (sum % 10)) % 10 === parseInt(code[7], 10);
  }
  return true;
}

export function extractBarcode(text) {
  // Correct OCR errors
  let correctedText = text
    .replace(/[OoQ]/g, '0')
    .replace(/[Ss]/g, '5')
    .replace(/[Il]/g, '1')
    .replace(/[Zz]/g, '2')
    .replace(/[B]/g, '8')
    .replace(/[G]/g, '6');

  const groups = correctedText.match(/\d+/g) || [];
  if (groups.length === 0) return null;

  let candidates = [];
  const targets = [13, 12, 8];

  for (let i = 0; i < groups.length; i++) {
    let concat = '';
    for (let j = i; j < Math.min(i + 8, groups.length); j++) {
      concat += groups[j];
      if (concat.length >= 8 && concat.length <= 14) {
        candidates.push(concat);
      }
      if (concat.length > 14) break;
    }
  }

  const long = groups.filter((g) => g.length >= 8);
  candidates.push(...long);

  const all = groups.join('');
  if (all.length >= 8 && all.length <= 14) candidates.push(all);

  if (candidates.length === 0) return null;
  candidates = [...new Set(candidates)];

  const validCandidates = candidates.filter((c) => targets.includes(c.length) && isValidBarcode(c));
  if (validCandidates.length > 0) {
    return validCandidates.sort((a, b) => b.length - a.length)[0];
  }

  // Fallback without checksum
  const targetCandidates = candidates.filter((c) => targets.includes(c.length));
  if (targetCandidates.length > 0) {
    return targetCandidates.sort((a, b) => b.length - a.length)[0];
  }

  return candidates.sort((a, b) => b.length - a.length)[0];
}

// Crop image area before OCR
export async function cropImageForBarcode(file) {
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      // Crop top & bottom border (keep center 80%)
      const cropX = Math.floor(img.width * 0.02);
      const cropY = Math.floor(img.height * 0.1);
      const cropW = Math.floor(img.width * 0.96);
      const cropH = Math.floor(img.height * 0.85);
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext('2d');
      ctx.filter = 'contrast(1.8) brightness(1.05) grayscale(1)';
      ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob || file), 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

// Native BarcodeDetector API (Android/Chrome)
export async function tryNativeBarcodeDetector(file) {
  if (!('BarcodeDetector' in window)) return null;
  let bitmap = null;
  try {
    const detector = new window.BarcodeDetector({ formats: ['ean_13', 'ean_8', 'code_128'] });
    bitmap = await createImageBitmap(file);
    const barcodes = await detector.detect(bitmap);
    if (barcodes.length > 0) return barcodes[0].rawValue;
  } catch (_) {
    // ignore detection error
  } finally {
    if (bitmap) {
      try {
        bitmap.close();
      } catch (_) {
        // ignore bitmap close error
      }
    }
  }
  return null;
}
